import { REDIRECT_COOKIE_PREFIX } from "@/const/links";

const REDIRECT_ATTEMPT_TTL_MS = 30_000;

export function getRedirectAttemptKey(linkId: string): string {
  return `${REDIRECT_COOKIE_PREFIX}${linkId}`;
}

/**
 * Check if a redirect was already attempted by reading a cookie or
 * sessionStorage flag. Both can throw SecurityError in restricted WebViews
 * (e.g. in-app browsers with cookies disabled), so we fall through silently.
 *
 * `value` tells apart flags that share a key, like the page an app bounce
 * left from (see lib/inAppEscape/appBounce.ts).
 */
export function hasRedirectBeenAttempted(key: string, value = "1"): boolean {
  const flag = `${key}=${value}`;
  try {
    if (document.cookie.split("; ").includes(flag)) return true;
  } catch (e) {
    console.warn("Cookie read blocked:", e);
  }
  try {
    const expiresAt = Number(sessionStorage.getItem(flag));
    if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return true;
    sessionStorage.removeItem(flag);
  } catch (e) {
    console.warn("SessionStorage read blocked:", e);
  }
  return false;
}

/** The cookie markRedirectAttempted sets, for a response that sets it instead. */
export function buildRedirectAttemptCookie(key: string, value = "1"): string {
  return `${key}=${value}; path=/; max-age=${REDIRECT_ATTEMPT_TTL_MS / 1000}; SameSite=Lax`;
}

/** Persist a redirect-attempted flag via cookie + sessionStorage fallback. */
export function markRedirectAttempted(key: string, value = "1"): void {
  const flag = `${key}=${value}`;
  try {
    document.cookie = buildRedirectAttemptCookie(key, value);
  } catch (e) {
    console.warn("Cookie write blocked:", e);
  }
  try {
    sessionStorage.setItem(flag, String(Date.now() + REDIRECT_ATTEMPT_TTL_MS));
  } catch (e) {
    console.warn("SessionStorage write blocked:", e);
  }
}
